
(() => {
  'use strict';

  const SELECTOR = '[data-animation]';
  const ROOT_CLASS = 'has-scroll-animations';

  if (window.GlobalScrollAnimations) return;

  let observer = null;
  let started = false;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const supportedAnimations = new Set([
    'fade-up',
    'fade-down',
    'fade-left',
    'fade-right',
    'fade-in',
    'zoom-in'
  ]);

  // Read numeric values in milliseconds.
  function getTime(element, attribute, fallback, max) {
    const raw = element.getAttribute(attribute);

    if (raw === null || raw.trim() === '') {
      return fallback;
    }

    const value = Number(raw);

    if (!Number.isFinite(value)) {
      return fallback;
    }

    return Math.min(Math.max(value, 0), max);
  }

  // Find animated siblings in the same container.
  function getStaggerIndex(element, animation) {
    const stagger = getTime(
      element,
      'data-animation-stagger',
      0,
      2000
    );

    if (stagger === 0) {
      return { delay: 0, stagger: 0 };
    }

    const parent = element.parentElement;

    if (!parent) {
      return { delay: 0, stagger: 0 };
    }

    const siblings = Array.from(parent.children).filter(
      (sibling) =>
        sibling.hasAttribute('data-animation') &&
        sibling.dataset.animation === animation
    );

    const index = siblings.indexOf(element);

    return {
      delay: Math.max(index, 0) * stagger,
      stagger
    };
  }

  function prepareElement(element) {
    const animation = element.dataset.animation;

    // Unsupported animations must remain visible.
    if (!supportedAnimations.has(animation)) {
      element.classList.add('is-visible');
      return;
    }

    const delay = getTime(
      element,
      'data-animation-delay',
      0,
      5000
    );

    const duration = getTime(
      element,
      'data-animation-duration',
      700,
      5000
    );

    const staggerData = getStaggerIndex(
      element,
      animation
    );

    const totalDelay = Math.min(
      delay + staggerData.delay,
      10000
    );

    element.style.setProperty(
      '--animation-delay',
      `${totalDelay}ms`
    );

    element.style.setProperty(
      '--animation-duration',
      `${duration}ms`
    );

    // Observe only elements not already revealed.
    if (!element.classList.contains('is-visible')) {
      observer.observe(element);
    }
  }

  function scan(root = document) {
    if (!observer || !root) return;

    if (
      root instanceof Element &&
      root.matches(SELECTOR)
    ) {
      prepareElement(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      root.querySelectorAll(SELECTOR).forEach(
        prepareElement
      );
    }
  }

  function start() {
    if (started) return;

    started = true;

    // Keep content visible in the Theme Editor.
    if (window.Shopify?.designMode) return;

    // Respect accessibility preferences.
    if (reducedMotion.matches) return;

    // Fail safely if IntersectionObserver is unavailable.
    if (!('IntersectionObserver' in window)) return;

    observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');

          // Animate each element only once.
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    // Prepare elements before enabling animation CSS.
    scan(document);

    document.documentElement.classList.add(ROOT_CLASS);
  }

  function onSectionLoad(event) {
    if (window.Shopify?.designMode) return;

    if (!observer) {
      start();
      return;
    }

    scan(event.target);
  }

  function onSectionUnload(event) {
    if (!observer) return;

    const section = event.target;

    if (section.matches?.(SELECTOR)) {
      observer.unobserve(section);
    }

    section.querySelectorAll?.(SELECTOR).forEach(
      (element) => {
        observer.unobserve(element);
      }
    );
  }

  function onMotionPreferenceChange(event) {
    if (!event.matches) return;

    // Reveal all observed elements if reduced motion
    // is enabled while the page is already open.
    if (observer) {
      observer.disconnect();
    }

    document.documentElement.classList.remove(ROOT_CLASS);

    document.querySelectorAll(SELECTOR).forEach(
      (element) => {
        element.classList.add('is-visible');
        element.style.removeProperty('--animation-delay');
        element.style.removeProperty('--animation-duration');
      }
    );
  }

  window.GlobalScrollAnimations = {
    refresh: scan
  };

  document.addEventListener(
    'shopify:section:load',
    onSectionLoad
  );

  document.addEventListener(
    'shopify:section:unload',
    onSectionUnload
  );

  if (reducedMotion.addEventListener) {
    reducedMotion.addEventListener(
      'change',
      onMotionPreferenceChange
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
